import classNames from "classnames";
import React from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaH4 from "../typography/H4/H4";
import "./kuika-page-header.scss";

/**
 * The heading row a tool panel opens with: what you are looking at on the left, an optional
 * control on the row's midline, and whatever commits or leaves it on the right.
 *
 * It is one component in two surfaces, because they are the same row and must not drift:
 *
 * - `inline` — a landing page's heading line, inside `KuikaListPage`'s own 32/48 gutter.
 * - `bar` — an editor's top bar: the strip glued to the top and both edges of the panel it
 *   opens in, the same chrome as the canvas column's `DesignSubBar`. Every detail editor
 *   (datasources, AI, code, email, scheduled jobs, API, config) wears this one, so the bar
 *   is where a tool's identity, its way back and its commit button always are.
 */

export type KuikaPageHeaderVariant = "inline" | "bar";

export interface KuikaPageHeaderProps {
  /**
   * Typically a 20px `KuikaSvg`; it inherits the header's neutral tint. Omitted on a bar
   * whose name control carries its own glyph (the switcher chip does).
   */
  icon?: React.ReactNode;
  /** The panel's name — not a call to action. */
  title: string;
  /** `bar` for an editor's top strip; `inline` (the default) for a landing page's heading. */
  variant?: KuikaPageHeaderVariant;
  /**
   * Renders a leading back control. Pass it on a SUB-page of a tool panel (an
   * "Add …" screen reached from the panel's own landing page); a landing page
   * has nowhere to go back to and leaves it off.
   *
   * In an editor this is the ONLY way out, so it runs the screen's guarded close — the
   * one that offers to save first — never a raw cancel.
   */
  onBack?: () => void;
  backLabel?: string;
  /**
   * Id for the back control. Worth passing when back is the ONLY way out of an editor:
   * it is then the target a suite that used to click a "Cancel" button has to resolve.
   */
  backTestId?: string;
  /** Anchor for a backend-authored onboarding tooltip step, for the same reason. */
  backOnboardingId?: string;
  /**
   * Centred in the header row, between the title and the actions.
   *
   * For a control that belongs to the whole page rather than to one end of it — an editor's
   * platform tabs, say. It is centred on the ROW, not on the space left over, so it does not
   * move as the title's length changes; the title is capped so the two cannot meet.
   */
  center?: React.ReactNode;
  /** Optional right-aligned controls. */
  actions?: React.ReactNode;
  titleTestId?: string;
  /**
   * Replaces the heading node with a caller-owned control — an inline rename, say.
   * `title` stays the accessible name, so pass it either way. Use this rather than
   * widening `title` to a node: the default heading is an `h4`, and an input nested
   * inside a heading is neither valid nor announced correctly.
   */
  renderTitle?: (title: string) => React.ReactNode;
  className?: string;
}

export const KuikaPageHeader = ({
  icon,
  title,
  variant = "inline",
  onBack,
  backLabel = "Back",
  backTestId,
  backOnboardingId,
  actions,
  center,
  titleTestId,
  renderTitle,
  className
}: KuikaPageHeaderProps) => {
  const isBar: boolean = variant === "bar";

  const backButton: React.ReactNode = onBack && (
    <KuikaButton
      type="tertiary"
      size="sm"
      isIcon
      // On the bar the arrow is chrome beside the name, not an accent: `tertiary` alone
      // paints an svg blue (`kuika-button.scss`), which the inline row never wanted either
      // but has always had.
      color={isBar ? "neutral" : undefined}
      aria-label={backLabel}
      data-testid={backTestId}
      data-onboarding-id={backOnboardingId}
      onClick={onBack}
    >
      <KuikaSvg type="arrows" name="arrow-left" width={isBar ? 16 : 18} height={isBar ? 16 : 18} />
    </KuikaButton>
  );

  /**
   * On the bar the control says where it goes, the way the canvas column's strip does: it is
   * the only exit, and an unlabelled arrow does not say which list it returns to.
   */
  const back: React.ReactNode =
    backButton && isBar ? (
      <KuikaTooltip position="bottom" content={backLabel}>
        {/* KuikaButton is a forwardRef, but the tooltip hands its ref to a real element
            either way; the span also keeps the trigger out of the left group's shrink. */}
        <span className="kuika-page-header__back">{backButton}</span>
      </KuikaTooltip>
    ) : (
      backButton
    );

  const titleNode: React.ReactNode = renderTitle ? (
    renderTitle(title)
  ) : (
    <KuikaH4 data-testid={titleTestId}>{title}</KuikaH4>
  );

  return (
    <div
      className={classNames(
        "kuika-page-header",
        `kuika-page-header--${variant}`,
        { "kuika-page-header--centered": !!center },
        className
      )}
    >
      {/* With a `center` control the row switches to a 3-column grid (see the scss): the tabs
          need a fixed middle column, so the back button, the icon and the title are folded
          into one shrinkable left group rather than three loose grid items. Without `center`
          the row stays the plain flex layout it always was — same flat children, no wrapper. */}
      {center ? (
        <span className="kuika-page-header__left">
          {back}
          {icon}
          {titleNode}
        </span>
      ) : (
        <>
          {back}
          {icon}
          {titleNode}
        </>
      )}
      {center && <span className="kuika-page-header__center">{center}</span>}
      {actions && <span className="kuika-page-header__actions">{actions}</span>}
    </div>
  );
};

export default KuikaPageHeader;
